<script setup lang="ts">
import BasicButton from '@/components/buttons/BasicButton.vue'
import Checkbox from '@/components/form/Checkbox.vue'
import { VueRecaptcha } from 'vue-recaptcha'
import { ref } from 'vue'
import { useContactStore } from '@/store/contact.store'
import useCss from '@/composables/useCss'
import { ElNotification } from 'element-plus'
import ImgComponent from '../media/ImgComponent.vue'

const store = useContactStore()

const formSection = store.getDescribe
const meeting = store.getBookMeeting
const recaptchaVerified = ref(false)
const config = useRuntimeConfig()
const { addClass, removeClass } = useCss()
const email = ref({
  name: '',
  surname: '',
  email: '',
  company: '',
  job: '',
  message: '',
  privacyPolicy: false,
  additionalInformation: false,
})

const handleSuccess = () => {
  recaptchaVerified.value = true
}
const handleError = () => {
  recaptchaVerified.value = false
}

const sitekey = computed(() => config.public.googleRecaptcha)

const sendEMail = async () => {
  if (email.value.privacyPolicy) {
    if (await store.sendEMail(email.value)) {
      email.value = {
        name: '',
        surname: '',
        email: '',
        company: '',
        job: '',
        message: '',
        privacyPolicy: false,
        additionalInformation: false,
      }
      ElNotification({
        title: 'Success',
        message: 'Email was sent',
        type: 'success',
      })
    } else {
      ElNotification({
        title: 'Error',
        message: 'Email has not been sent',
        type: 'error',
      })
    }
  } else {
    addClass('shake--error', 'shake')
    setTimeout(() => {
      removeClass('shake--error', 'shake')
    }, 820)
  }
}
</script>

<template>
  <div :class="{ 'section-maker': meeting }">
    <form
      class="flex flex-col mb-0"
      v-if="formSection"
      @submit.prevent="sendEMail"
    >
      <h1
        class="color-title-dark text-[22px] md:text-[30px] leading-8 md:leading-10 font-bold text-left my-4 lg:mt-0 lg:mb-10"
      >
        {{ formSection.title }}
      </h1>
      <div class="flex flex-col lg:grid gap-2 md:grid-cols-2">
        <ElInput
          type="text"
          name="name"
          v-model="email.name"
          placeholder="Your name*"
          :cols="1"
          required
          size="large"
        />
        <ElInput
          type="text"
          name="surname"
          v-model="email.surname"
          placeholder="Your surname*"
          required
          size="large"
        />
        <ElInput
          type="email"
          name="email"
          v-model.trim="email.email"
          placeholder="Type your email*"
          class="col-span-2"
          size="large"
          required
        />
        <ElInput
          type="text"
          :cols="2"
          name="company"
          v-model="email.company"
          placeholder="Company name*"
          size="large"
          required
        />
        <ElInput
          type="text"
          name="job"
          v-model="email.job"
          placeholder="Type your job title"
          size="large"
        />
        <ElInput
          v-model="email.message"
          placeholder="How can we help you?"
          type="textarea"
          size="large"
          class="col-span-2"
          :autosize="{ minRows: 6 }"
          resize="none"
        />
      </div>
      <div class="flex flex-col mt-4">
        <div>
          <sup>
            <strong>*Required information</strong>
          </sup>
        </div>
        <label id="shake--error" for="privacy" class="flex align-center lg:mb-4">
          <Checkbox
            name="privacy"
            id="privacy"
            :class="{ required: !email.privacyPolicy }"
            v-model="email.privacyPolicy"
          />
          <p
            class="ml-2 mt-1 text-[14px] md:text-[16px] leading-[22px] font-medium"
          >
            *Agree to the
            <a
              :href="store.getPrivacyPolicy ?? '/contact'"
              aria-label="Privacy Policy"
              target="_blank"
              class="link underline font-medium"
            >
              Code Flyers privacy policy
            </a>
          </p>
        </label>
        <label for="newsletter" class="flex align-center leading-[22px]">
          <Checkbox
            name="newsletter"
            id="newsletter"
            v-model="email.additionalInformation"
          />
          <p class="ml-2 mt-1 font-medium text-[14px] md:text-[16px]">
            I want to receive additional information about Code Flyers
            <a href="/services" class="link underline font-medium"> services </a>
          </p>
        </label>
        <div class="flex flex-col xl:flex-row w-fit xl:w-full gap-6 pt-6">
          <div>
            <VueRecaptcha
              class="flex w-fit origin-top-left scale-75 xs:scale-[0.85] md:scale-100"
              :sitekey="sitekey"
              language="en"
              @verify="handleSuccess"
              @error="handleError"
              @expired="handleError"
            />
          </div>
          <BasicButton
            btnClass="font-bold w-fit my-auto lg:my-auto px-20"
            :active="recaptchaVerified"
            type="submit"
            value="submit"
          >
            {{ formSection.buttonText }}
          </BasicButton>
        </div>
      </div>
    </form>
    <div v-if="meeting" class="flex flex-col items-center m-auto card--blue rounded--1 w-fit p-6" >
      <h1 class="color-title-dark text-[30px] leading-10 font-bold text-left">
        {{ meeting.title }}
      </h1>
      <ImgComponent :src="meeting.person" />
      <h2 class="color-title-dark text-[22px] leading-8 font-bold text-left" >
        {{ meeting.name }}
      </h2>
      <p class="color-describe-grey text-xs" >
        {{ meeting.describe }}
      </p>
      <NuxtLink v-if="meeting.btnName" :to="meeting.url">
        <BasicButton btnClass="font-bold w-fit my-auto lg:my-auto px-20">
          {{ meeting.btnName }}
        </BasicButton>
      </NuxtLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
a.link {
  color: #023f87;
}

:deep(textarea) {
  height: 100px;
}

.required {
  :deep(.el-checkbox__inner) {
    border: 2px solid red;
  }
}

.section-maker {
  display: grid;
  grid-template-columns: 3fr 2fr;

  @media screen and (max-width: 1358px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

.shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}
</style>
